import React from 'react';

/**
 * Shown with every assessment result: on the screen after each assessment and on the report.
 *
 * The assessments measure things like trauma, isolation and mental health, and until this existed
 * a flagged result offered a resource link and nothing else. Two things belong with any result:
 * that it is not a diagnosis, and where to turn if someone is in crisis right now.
 *
 * It is shown for every result rather than only flagged ones. The screen after each assessment
 * does not reliably know whether the result was flagged, and a notice that appears only on bad
 * news reads as an alarm; one that is always there reads as care.
 *
 * Wording approved by Robert (2026-10-11).
 */
// crisisOnly: pages that show no results (guidance, training) carry only the crisis line, since
// "these results are not a diagnosis" would make no sense there. Both texts are Robert's approved
// wording; nothing new is said.
function SupportNotice({ compact = false, crisisOnly = false }) {
  return (
    <div
      className={'support-notice border rounded bg-light text-gray-700 text-start mx-auto '
        + (compact ? 'p-4 fs-6 mt-8' : 'p-6 fs-5 mt-15')}
      style={{ maxWidth: '760px' }}
      role="note"
      aria-label="About your results"
    >
      {crisisOnly ? null : (
      <p className="mb-2">
        These results are not a diagnosis. They point to areas where you may be facing obstacles,
        so you can decide what to work on. If anything here is weighing on you, consider talking
        it through with a counselor, your doctor, or someone you trust.
      </p>
      )}
      <p className="mb-0 fw-semibold">
        If you are in crisis or thinking about harming yourself, call or text{' '}
        <a href="tel:988">988</a> (Suicide &amp; Crisis Lifeline, US) or{' '}
        <a href="https://988lifeline.org" target="_blank" rel="noopener noreferrer">chat online</a>.
        {' '}Outside the US, call your local emergency number.
      </p>
    </div>
  );
}

export default SupportNotice;
